import { useCallback, useEffect } from 'react';
import { VIMEO_OEMBED_API } from '../../constants';
import Player from '@vimeo/player';

export const useVimeoPlayer = ({
  url,
  autoPlay,
  loop,
  mute,
  enabledTitle,
  startTime,
  endTime,
  ref,
}: {
  url?: string;
  autoPlay?: boolean;
  loop?: boolean;
  mute?: boolean;
  enabledTitle?: boolean;
  startTime?: number;
  endTime?: number;
  ref?: React.RefObject<HTMLDivElement>;
}) => {
  const fetchAndSetAspectRatio = useCallback(async (): Promise<void> => {
    if (!url || !ref?.current) {
      return;
    }

    try {
      const oembedUrl = `${VIMEO_OEMBED_API}?url=${encodeURIComponent(url)}`;
      const response = await fetch(oembedUrl);

      if (!response.ok) {
        return;
      }

      const videoData = await response.json();

      if (videoData.width && videoData.height) {
        ref.current?.style.setProperty(
          '--aspect-ratio',
          `${videoData.width}/${videoData.height}`,
        );
      }
    } catch {
      ref.current?.style.setProperty('--aspect-ratio', '16 / 9');
    }
  }, [url, ref]);

  useEffect(() => {
    if (!url || !ref?.current) {
      return;
    }

    let player: Player | undefined;

    const initializePlayer = async (): Promise<void> => {
      if (!ref.current) {
        return;
      }

      ref.current.style.setProperty('--visibility', 'hidden');

      await fetchAndSetAspectRatio();

      if (!ref.current) {
        return;
      }

      player = new Player(ref.current, {
        url: url!,
        autoplay: autoPlay,
        loop,
        muted: autoPlay || mute,
        controls: true,
        title: enabledTitle,
        /*
            player supports more parameters, but types are incomplete
            check for complete api here:
            https://developer.vimeo.com/player/sdk/embed
        */
        // @ts-expect-error
        start_time: startTime,
        end_time: endTime,
      });

      await player.ready();
      ref.current?.style.setProperty('--visibility', 'visible');
    };

    void initializePlayer();

    return () => {
      void player?.destroy();
    };
  }, [
    url,
    loop,
    autoPlay,
    mute,
    enabledTitle,
    startTime,
    endTime,
    fetchAndSetAspectRatio,
    ref,
  ]);
};
